import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { brl, fdatetime } from "@/lib/format";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Users, Package, BadgeDollarSign, Receipt } from "lucide-react";

export default function SearchCommand({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (!open) {
      setQ("");
      setResults(null);
    }
  }, [open]);

  useEffect(() => {
    if (!q.trim()) {
      setResults(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/search", { params: { q } });
        setResults(r.data);
      } catch {
        setResults(null);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = (path) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0 max-w-xl" data-testid="global-search-dialog">
        <DialogTitle className="sr-only">Busca global</DialogTitle>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar alunos, produtos, vendas..."
            value={q}
            onValueChange={setQ}
            data-testid="global-search-input"
          />
          <CommandList>
            {q && results && (
              <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>
            )}
            {results?.students?.length > 0 && (
              <CommandGroup heading="Alunos">
                {results.students.map((s) => (
                  <CommandItem
                    key={s.id}
                    onSelect={() => go(`/alunos?q=${encodeURIComponent(s.name)}`)}
                    data-testid={`search-student-${s.id}`}
                  >
                    <Users className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span>{s.name}</span>
                    {s.plan_name && (
                      <span className="ml-auto text-xs text-muted-foreground">{s.plan_name}</span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {results?.products?.length > 0 && (
              <CommandGroup heading="Produtos">
                {results.products.map((p) => (
                  <CommandItem
                    key={p.id}
                    onSelect={() => go(`/produtos?q=${encodeURIComponent(p.name)}`)}
                    data-testid={`search-product-${p.id}`}
                  >
                    <Package className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span>{p.name}</span>
                    <span className="ml-auto num text-xs text-muted-foreground">{brl(p.price)}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {results?.plans?.length > 0 && (
              <CommandGroup heading="Planos">
                {results.plans.map((p) => (
                  <CommandItem key={p.id} onSelect={() => go("/planos")} data-testid={`search-plan-${p.id}`}>
                    <BadgeDollarSign className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span>{p.name}</span>
                    <span className="ml-auto num text-xs text-muted-foreground">{brl(p.price)}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {results?.sales?.length > 0 && (
              <CommandGroup heading="Vendas">
                {results.sales.map((s) => (
                  <CommandItem key={s.id} onSelect={() => go("/movimentacoes")} data-testid={`search-sale-${s.id}`}>
                    <Receipt className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span>{s.student_name || "Venda avulsa"}</span>
                    <span className="ml-auto num text-xs text-muted-foreground">
                      {brl(s.total)} · {fdatetime(s.created_at)}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
